/**
 * Socket.io service for WebSocket communication
 * Manages real-time connection with backend
 */

import { io, Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '../types';

class SocketService {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private sessionId: string | null = null;
  
  /**
   * Connect to WebSocket server
   * Each session gets its own room for isolated conversations
   */
  connect(sessionId: string): Socket<ServerToClientEvents, ClientToServerEvents> {
    // If already connected to same session, return existing socket
    if (this.socket && this.sessionId === sessionId) {
      return this.socket;
    }
    
    // Disconnect existing socket if session changed
    if (this.socket) {
      this.disconnect();
    }
    
    // Create new socket connection
    const wsUrl = import.meta.env.VITE_WS_URL || 'http://localhost:3001';
    
    this.socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      query: { sessionId }
    });
    
    this.sessionId = sessionId;
    
    // Set up connection event handlers
    this.socket.on('connect', () => {
      console.log('WebSocket connected, session:', sessionId);
    });
    
    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
    });
    
    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
    });
    
    return this.socket;
  }
  
  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.sessionId = null;
    }
  }
  
  /**
   * Get current socket instance
   */
  getSocket(): Socket<ServerToClientEvents, ClientToServerEvents> | null {
    return this.socket;
  }
  
  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }
  
  /**
   * Emit event with type safety
   */
  emit<K extends keyof ClientToServerEvents>(
    event: K,
    ...args: Parameters<ClientToServerEvents[K]>
  ): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, ...args);
    } else {
      console.error('Cannot emit event: Socket not connected');
    }
  }
}

// Export singleton instance
export const socketService = new SocketService();